import { Button, Popconfirm, Space, Typography, Upload } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { MdDelete, MdOutlineFileUpload } from 'react-icons/md'

import { restClient } from '@/Core'
import { useNotification } from '@/Core/Hooks/useNotification'
import { BaseViewFieldTemplate } from '@/Ship/Templates/Form/BaseViewFieldTemplate'

import { getErrorMessage } from '../Helpers/getErrorMessage'

import type { TaskCrudContextLike, TaskFormContextLike } from '../Contracts/Crud.types'
import type { TaskFile } from '../Contracts/Task.types'
import type { UploadProps } from 'antd'
import type { FC, ReactNode } from 'react'

type UploadRequestOption = Parameters<NonNullable<UploadProps['customRequest']>>[0]

/** Ship-шаблон — .jsx без типів; children у пропсах відновлюємо аліасом. */
const ViewFieldTemplate = BaseViewFieldTemplate as FC<{ field: { label: string }; children?: ReactNode }>

/** Клієнтський ліміт розміру файла (ФАКТ nginx = 10 MB, хвіст 08.2). */
const UPLOAD_MAX_SIZE_MB = 10

const { Text } = Typography
const { Dragger } = Upload

interface Props {
    // Explicit `| undefined` for exactOptionalPropertyTypes: TaskFormTemplate
    // passes possibly-undefined contexts straight through.
    formContext?: TaskFormContextLike | undefined
    crudContext?: TaskCrudContextLike | undefined
}

/**
 * Файли задачі на view- та edit-картці (хвіст 08.1, сценарій С5).
 *
 * Ядровий FileInput показує кнопку видалення лише в edit-режимі і б'є нею у
 * `file['@id']` — а файли задачі роутлес (їхній `@id` — fallback по uuid, не
 * DELETE-роут), тож на картці видалення було мертвим. Тут рендеримо файли своїм
 * показом (лінк на відкриття + кнопка delete), і кнопка б'є у НАШ DELETE-роут
 * `/services/tasks/files/{uuid}` (N12). Видимість кнопки — за `_permissions.file_upload`
 * задачі (на видаленій = false → read-only). Після видалення ховаємо файл локально
 * і перезавантажуємо картку.
 *
 * В edit-режимі показуємо ЗОНУ перетягування (`Upload.Dragger` з ядровою
 * розміткою `file-input-upload*` — той самий вигляд і клас, що в ядровому
 * FileInput): файл можна і перетягнути, і вибрати кліком. Upload (N11) кладе
 * відповідь у `values.files`, а трансформер збереження ресурсу
 * (`onBeforeTransformSendSaveRequest`, дискримінатор `createdAt`) прикріплює
 * нові файли через `fileUuids` на «Зберегти».
 */
export const TaskFilesView = observer(({ formContext, crudContext }: Props) => {
    const { showSuccess, showError } = useNotification()
    const [removed, setRemoved] = useState<Set<string>>(new Set())
    const [uploading, setUploading] = useState(false)

    const files = (formContext?.values?.files ?? []).filter((f): f is TaskFile => !!f && !removed.has(f.uuid))
    const canUpload = formContext?.values?._permissions?.file_upload === true
    const canDelete = canUpload
    // Edit-режим картки: додаємо кнопку «Файл» — upload (N11) кладе відповідь у
    // values.files, а трансформер збереження (onBeforeTransformSendSaveRequest,
    // дискримінатор createdAt) сам прикріпить нові через fileUuids на «Зберегти»
    const isEdit = formContext?.isEditable === true && !!formContext?.values?.uuid

    const handleUpload = (options: UploadRequestOption): void => {
        const file = options.file as File
        if (file.size > UPLOAD_MAX_SIZE_MB * 1024 * 1024) {
            showError(`Файл завеликий. Максимальний розмір: ${UPLOAD_MAX_SIZE_MB} MB.`)
            options.onError?.(new Error('too large'))
            return
        }
        setUploading(true)
        const body = new FormData()
        body.append('file', file, file.name)
        restClient
            .post('/services/tasks/files', { body }, { isNotHeaderAddContentType: true })
            ?.then((raw: unknown) => {
                const uploaded = raw as TaskFile
                if (uploaded?.uuid) {
                    formContext?.setValue?.('files', [...(formContext?.values?.files ?? []), uploaded])
                }
                options.onSuccess?.({})
            })
            .catch((e: unknown) => {
                showError(getErrorMessage(e) || 'Не вдалося завантажити файл')
                options.onError?.(e as Error)
            })
            .finally(() => setUploading(false))
    }

    const handleDelete = (uuid: string): void => {
        restClient
            .delete(`/services/tasks/files/${uuid}`)
            ?.then(() => {
                setRemoved((prev) => new Set(prev).add(uuid))
                showSuccess('Файл видалено')
                crudContext?.actions?.reloadData?.()
            })
            .catch((e: unknown) => showError(getErrorMessage(e) || 'Не вдалося видалити файл'))
    }

    const list =
        files.length === 0 ? null : (
            <Space direction="vertical" size={4} style={{ display: 'flex' }}>
                {files.map((file) => (
                    <Space key={file.uuid} size={8}>
                        <a href={file.url} target="_blank" rel="noreferrer">
                            {file.originalName}
                        </a>
                        {canDelete && (
                            <Popconfirm
                                title="Видалити файл?"
                                okText="Так"
                                cancelText="Ні"
                                onConfirm={() => handleDelete(file.uuid)}
                            >
                                <Button
                                    type="text"
                                    size="small"
                                    danger
                                    icon={<MdDelete />}
                                    aria-label={`Видалити файл ${file.originalName}`}
                                />
                            </Popconfirm>
                        )}
                    </Space>
                ))}
            </Space>
        )

    // Перегляд — тим самим шаблоном, що решта полів картки: підпис дрібно
    // великими літерами, лінія під полем, «—», коли файлів немає
    if (!isEdit) {
        return <ViewFieldTemplate field={{ label: 'Файли' }}>{list}</ViewFieldTemplate>
    }

    // Редагування — розміткою поля форми (`BaseFieldTemplate`): підпис той
    // самий, що над сусідніми полями
    return (
        <div className="form-field form-field-default">
            <div className="field-label">
                <span className="field-label-title">Файли</span>
            </div>
            {list ?? <Text type="secondary">Файлів ще немає</Text>}
            {canUpload && (
                <div className="file-input-upload" style={{ marginTop: 12 }}>
                    <Dragger customRequest={handleUpload} showUploadList={false} multiple disabled={uploading}>
                        <section className="file-input-upload-area">
                            <div className="file-input-upload-icon">
                                <MdOutlineFileUpload size={22} />
                            </div>
                            <div className="file-input-upload-text">
                                <label className="file-input-upload-label-primary">
                                    <span className="file-input-upload-label-normal">Перетягніть файл або </span>
                                    <span className="file-input-upload-label-link">Завантажте тут</span>
                                </label>
                                <label className="file-input-upload-label-secondary">
                                    <span>{uploading ? 'Завантаження…' : 'Оберіть файл для завантаження'}</span>
                                    <span className="file-input-upload-label-hint">
                                        <span>Макс. розмір: {UPLOAD_MAX_SIZE_MB} МБ</span>
                                    </span>
                                </label>
                            </div>
                        </section>
                    </Dragger>
                </div>
            )}
        </div>
    )
})
